import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "border-b border-white/10 bg-black/70 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10" aria-label="Main navigation">
        <a href="#home" data-testid="nav-logo" className="group flex items-baseline gap-2">
          <span className="font-display text-lg font-semibold uppercase tracking-tight text-neutral-100">
            Sovan<span className="text-electric"> Gourav Rout</span>
          </span>
        </a>

        <ul className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className="group relative text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 transition-colors duration-300 hover:text-neutral-100"
              >
                {link.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-electric transition-[width] duration-300 group-hover:w-full" aria-hidden="true" />
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="nav-cta-button"
            className="hidden rounded-full bg-electric px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.15em] text-[#050505] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#ffa733] lg:inline-block"
          >
            Let's Talk
          </a>
          <button
            type="button"
            data-testid="nav-mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="rounded-md p-2 text-neutral-200 lg:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <ul className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="block py-3 font-display text-xl font-semibold uppercase tracking-tight text-neutral-200 transition-colors hover:text-electric"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              <li className="pt-4">
                <a
                  href="#contact"
                  data-testid="nav-mobile-cta-button"
                  onClick={() => setOpen(false)}
                  className="inline-block rounded-full bg-electric px-6 py-3 text-xs font-extrabold uppercase tracking-[0.15em] text-[#050505]"
                >
                  Let's Talk
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
